import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DotsThreeVertical,
  Download,
  PaperPlaneTilt,
  FilePdf,
  CircleNotch,
  PhoneCall,
  ArrowCounterClockwise,
  CheckCircle,
  ArrowBendUpLeft,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  generateFeeNotePdf,
  sendFeeNote,
  approveFeeNote,
  rejectFeeNote,
  cancelFeeNote,
  refundFeeNote,
  type FeeNoteListRow,
  type FeeNoteDetail,
} from '@/lib/feeNotesApi';
import { api, getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { FeeChaseLogModal } from './FeeChaseLogModal';
import { CreditNoteModal } from '@/components/shared/CreditNoteModal';

interface Props {
  feeNote: FeeNoteListRow | FeeNoteDetail;
  onChanged?: (fn: FeeNoteDetail) => void;
}

export function FeeNoteActionsButton({ feeNote, onChanged }: Props) {
  const qc = useQueryClient();
  const memberType = useAuthStore((s) => s.user?.memberType);
  const [open, setOpen] = useState(false);
  const [chaseOpen, setChaseOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [busy, setBusy] = useState<'generate' | 'download' | 'send' | 'approve' | 'reject' | 'cancel' | 'refund' | null>(null);

  const generateMut = useMutation({
    mutationFn: () => generateFeeNotePdf(feeNote.id),
    onMutate: () => setBusy('generate'),
    onSuccess: (result) => {
      toast.success(`PDF generated (${(result.sizeBytes / 1024).toFixed(1)} KB)`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      onChanged?.(result.feeNote);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not generate PDF')),
    onSettled: () => {
      setBusy(null);
      setOpen(false);
    },
  });

  const downloadMut = useMutation({
    mutationFn: async () => {
      // Fetch PDF as a blob (keeps auth header) then trigger a browser file-save.
      const res = await api.get(`/fee-notes/${feeNote.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${feeNote.feeNoteNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      return feeNote.feeNoteNumber;
    },
    onMutate: () => setBusy('download'),
    onSuccess: (num) => toast.success(`Downloading ${num}.pdf`),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not open PDF')),
    onSettled: () => {
      setBusy(null);
      setOpen(false);
    },
  });

  const sendMut = useMutation({
    mutationFn: () => sendFeeNote(feeNote.id),
    onMutate: () => setBusy('send'),
    onSuccess: (result) => {
      toast.success(`Sent ${feeNote.feeNoteNumber} — invoice emailed and status flipped to ${result.status}`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      onChanged?.(result);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not send fee note')),
    onSettled: () => {
      setBusy(null);
      setOpen(false);
    },
  });

  const approveMut = useMutation({
    mutationFn: () => approveFeeNote(feeNote.id),
    onMutate: () => setBusy('approve'),
    onSuccess: (result) => {
      toast.success(`Approved ${feeNote.feeNoteNumber} — the clerk can now send it`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      onChanged?.(result);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not approve fee note')),
    onSettled: () => { setBusy(null); setOpen(false); },
  });

  const rejectMut = useMutation({
    mutationFn: (reason: string) => rejectFeeNote(feeNote.id, reason),
    onMutate: () => setBusy('reject'),
    onSuccess: (result) => {
      toast.success(`Returned ${feeNote.feeNoteNumber} for amendment`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      onChanged?.(result);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not return fee note')),
    onSettled: () => { setBusy(null); setOpen(false); },
  });

  const handleReturn = () => {
    const reason = window.prompt('Return this fee note to the clerk for amendment. What needs changing?');
    if (reason && reason.trim()) rejectMut.mutate(reason.trim());
  };

  const cancelMut = useMutation({
    mutationFn: (reason: string) => cancelFeeNote(feeNote.id, reason),
    onMutate: () => setBusy('cancel'),
    onSuccess: (result) => {
      toast.success(`Cancelled ${feeNote.feeNoteNumber} — the billed work is free to re-bill`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      onChanged?.(result);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not cancel fee note')),
    onSettled: () => { setBusy(null); setOpen(false); },
  });
  const handleCancel = () => {
    const reason = window.prompt('Cancel this fee note (raised in error)? Its billed work is freed for a replacement. Reason:');
    if (reason && reason.trim()) cancelMut.mutate(reason.trim());
  };
  // Cancellable only while no money is against it (else use a credit note).
  const isCancellable =
    ['draft', 'pending_approval', 'approved', 'sent', 'overdue', 'send_failed'].includes(feeNote.status)
    && Number(feeNote.amountPaid) === 0 && Number(feeNote.creditedAmount) === 0;

  const netCashHeld = Number(feeNote.amountPaid) - Number((feeNote as { refundedAmount?: string }).refundedAmount ?? 0);
  const isRefundable = netCashHeld > 0;
  const refundMut = useMutation({
    mutationFn: ({ amount, reason }: { amount: number; reason: string }) => refundFeeNote(feeNote.id, amount, reason),
    onMutate: () => setBusy('refund'),
    onSuccess: (result) => {
      toast.success(`Refund recorded on ${feeNote.feeNoteNumber}`);
      qc.invalidateQueries({ queryKey: ['fee-notes'] });
      onChanged?.(result);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not record refund')),
    onSettled: () => { setBusy(null); setOpen(false); },
  });
  const handleRefund = () => {
    const raw = window.prompt(`Refund cash to the payer (up to £${netCashHeld.toFixed(2)} held). Amount:`);
    if (!raw) return;
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Enter a valid amount'); return; }
    const reason = window.prompt('Reason for the refund?');
    if (reason && reason.trim()) refundMut.mutate({ amount, reason: reason.trim() });
  };

  const isWorking = busy !== null;
  const isCancelled = feeNote.status === 'cancelled';
  const isSending = feeNote.status === 'sending'; // dispatch in progress — disable all actions
  const isPendingApproval = feeNote.status === 'pending_approval';
  // Only the barrister whose fee it is (or an admin) approves; the server further
  // enforces it's THIS matter's barrister. Clerks never see approve/return.
  const canApprove = memberType === 'admin' || memberType === 'silk' || memberType === 'junior';
  const isChaseWorthy = ['sent', 'overdue', 'partially_paid'].includes(feeNote.status);
  const isCreditWorthy = ['sent', 'overdue', 'partially_paid', 'partially_credited'].includes(feeNote.status);
  // 'send_failed' is re-sendable — clerk must retry; treat like 'approved'. A note
  // awaiting counsel approval is NOT sendable until it is approved.
  const isSendDisabled = isCancelled || isSending || isPendingApproval || feeNote.status === 'written_off';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        disabled={isWorking}
        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {isWorking ? (
          <CircleNotch size={14} className="animate-spin" />
        ) : (
          <DotsThreeVertical size={16} weight="bold" />
        )}
      </button>

      {open && !isWorking && (
        <>
          {/* Click-outside catcher */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="absolute right-0 top-9 z-50 w-56 overflow-hidden rounded-md border border-border bg-card shadow-lg"
          >
            {isPendingApproval && canApprove && (
              <>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => approveMut.mutate()}
                  className="flex w-full items-center gap-2 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-left text-sm font-medium text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 dark:bg-emerald-500/15"
                >
                  <CheckCircle size={13} weight="fill" />
                  <span className="flex-1">Approve fee note</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleReturn}
                  className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted"
                >
                  <ArrowBendUpLeft size={13} weight="duotone" />
                  <span className="flex-1">Return for amendment</span>
                </button>
                <div className="border-t border-border" />
              </>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => downloadMut.mutate()}
              disabled={isCancelled}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={13} weight="duotone" />
              <span className="flex-1">Download PDF</span>
              {!feeNote.pdfUrl && <span className="text-[10px] text-muted-foreground">generates</span>}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => generateMut.mutate()}
              disabled={isCancelled}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FilePdf size={13} weight="duotone" />
              <span className="flex-1">Regenerate PDF</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => sendMut.mutate()}
              disabled={isSendDisabled}
              className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PaperPlaneTilt size={13} weight="duotone" />
              <span className="flex-1">
                {feeNote.status === 'send_failed'
                  ? 'Retry send'
                  : feeNote.sentAt
                  ? 'Re-send to solicitor'
                  : 'Send to solicitor'}
              </span>
            </button>
            {isChaseWorthy && (
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); setChaseOpen(true); }}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <PhoneCall size={13} weight="duotone" />
                <span className="flex-1">Chase contact log</span>
              </button>
            )}
            {isCreditWorthy && (
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); setCreditOpen(true); }}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <ArrowCounterClockwise size={13} weight="duotone" />
                <span className="flex-1">Issue credit note</span>
              </button>
            )}
            {isRefundable && (
              <button
                type="button"
                role="menuitem"
                onClick={handleRefund}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <ArrowCounterClockwise size={13} weight="duotone" />
                <span className="flex-1">Record refund</span>
              </button>
            )}
            {isCancellable && (
              <button
                type="button"
                role="menuitem"
                onClick={handleCancel}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-red-700 dark:text-red-300 hover:bg-red-50 dark:bg-red-500/10"
              >
                <X size={13} weight="bold" />
                <span className="flex-1">Cancel fee note</span>
              </button>
            )}
          </div>
        </>
      )}

      {chaseOpen && (
        <FeeChaseLogModal
          feeNoteId={feeNote.id}
          feeNoteNumber={feeNote.feeNoteNumber}
          onClose={() => setChaseOpen(false)}
        />
      )}

      {creditOpen && (
        <CreditNoteModal
          feeNoteId={feeNote.id}
          feeNoteNumber={feeNote.feeNoteNumber}
          feeNoteTotal={feeNote.total}
          amountPaid={feeNote.amountPaid}
          creditedAmount={feeNote.creditedAmount}
          outstanding={feeNote.outstanding}
          currency={feeNote.currency}
          onClose={() => setCreditOpen(false)}
          onCreated={() => {
            setCreditOpen(false);
            qc.invalidateQueries({ queryKey: ['fee-notes'] });
            onChanged && qc.invalidateQueries({ queryKey: ['fee-note', feeNote.id] });
          }}
        />
      )}
    </div>
  );
}
